/**
 * SignStampUploader Component
 * Handles sign/stamp image upload and preview for invoices
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { validateImageFile } from '@/utils/file-validation';

interface SignStampUploaderProps {
  currentSignStamp?: string;
  onSignStampChange: (signStampData: string | null) => void;
  className?: string;
}

export function SignStampUploader({ currentSignStamp, onSignStampChange, className = '' }: SignStampUploaderProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = async (file: File): Promise<boolean> => {
    // Use enhanced validation with magic byte checking
    const validation = await validateImageFile(file);

    if (!validation.valid) {
      setError(validation.error || t('invoice.editor.signStampUpload.error.invalidType'));
      return false;
    }

    setError(null);
    return true;
  };

  const processFile = async (file: File) => {
    const isValid = await validateFile(file);
    if (!isValid) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      onSignStampChange(base64String);
    };
    reader.onerror = () => {
      setError(t('invoice.editor.signStampUpload.error.readFailed'));
    };
    reader.readAsDataURL(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleRemove = () => {
    onSignStampChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`sign-stamp-uploader ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {currentSignStamp ? (
        <div className="sign-stamp-preview">
          <img
            src={currentSignStamp}
            alt="Company sign/stamp"
            className="max-w-full max-h-32 object-contain"
          />
          <div className="sign-stamp-actions flex gap-2 mt-2">
            <button
              type="button"
              onClick={handleClick}
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              {t('invoice.editor.signStampUpload.changeSignStamp')}
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              {t('invoice.editor.signStampUpload.remove')}
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            sign-stamp-dropzone border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${isDragging
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            }
          `}
        >
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
            aria-hidden="true"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="mt-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-semibold text-blue-600 dark:text-blue-400">
                {t('invoice.editor.signStampUpload.clickToUpload')}
              </span>{' '}
              {t('invoice.editor.signStampUpload.dragAndDrop')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              {t('invoice.editor.signStampUpload.formats')}
            </p>
          </div>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400 mt-2">{error}</p>
      )}
    </div>
  );
}
